'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { GA_EVENTS, GA_NAV_METHOD, GA_PARAMS } from '@/constants/analytics';
import { HERO_REVEAL_PROPS, HERO_SWIRL_CLIP_TARGET_PROPS } from '@/constants/heroScene';
import {
  STICKY_NAV_SCROLL_PIN_KEY,
  STICKY_NAV_SCROLL_PIN_TIMEOUT_MS,
} from '@/constants/stickyNav';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { scrollToSection } from '@/lib/scroll';

import styles from './StickyNav.module.css';
import { useStickyNavPillPosition } from './useStickyNavPillPosition';

import type { Chapter } from '@/components/layout/Section';

export type StickyNavItem = {
  id: Chapter;
  label: string;
  href: string;
};

type StickyNavProps = {
  items: StickyNavItem[];
  ariaLabel: string;
};

const chapterIds: Chapter[] = ['introduction', 'state', 'pressure', 'response'];

const STICKY_NAV_OFFSET = 0;

/** Matches IO `rootMargin` bottom — top 45% of the viewport. */
const SCROLL_SPY_VIEWPORT_RATIO = 0.45;

const SCROLL_PIN_USER_DATA: Record<string, unknown> = {
  [STICKY_NAV_SCROLL_PIN_KEY]: true,
};

function getChapterFromScrollPosition(): Chapter {
  const marker = window.scrollY + window.innerHeight * SCROLL_SPY_VIEWPORT_RATIO;
  let active: Chapter = chapterIds[0];

  for (const id of chapterIds) {
    const el = document.getElementById(id);
    if (!el) continue;

    const sectionTop = el.getBoundingClientRect().top + window.scrollY;
    if (sectionTop <= marker) {
      active = id;
    }
  }

  return active;
}

export function StickyNav({ items, ariaLabel }: StickyNavProps) {
  const [activeChapter, setActiveChapter] = useState<Chapter>('introduction');
  const prefersReducedMotion = usePrefersReducedMotion();
  const ratiosRef = useRef<Partial<Record<Chapter, number>>>({});
  /** While set, IO scroll-spy must not override the optimistic active chapter. */
  const pinnedChapterRef = useRef<Chapter | null>(null);
  const pinGenerationRef = useRef(0);
  const pinCleanupRef = useRef<(() => void) | null>(null);
  const { listRef, setLinkRef, pill } = useStickyNavPillPosition(activeChapter);

  const syncActiveFromSpy = useCallback(() => {
    let bestId: Chapter = chapterIds[0];
    let bestRatio = -1;

    for (const id of chapterIds) {
      const ratio = ratiosRef.current[id] ?? 0;
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestId = id;
      }
    }

    if (bestRatio <= 0) {
      bestId = getChapterFromScrollPosition();
    }

    setActiveChapter(bestId);
  }, []);

  const releasePin = useCallback(
    (generation: number, reason: 'complete' | 'interrupt' | 'timeout') => {
      if (pinGenerationRef.current !== generation) return;
      if (pinnedChapterRef.current === null) return;

      pinnedChapterRef.current = null;
      pinCleanupRef.current?.();
      pinCleanupRef.current = null;

      if (reason === 'complete') {
        // Keep the optimistic target; IO ratios can lag one frame behind Lenis.
        return;
      }

      // User took over or safety timeout — reconcile from real scroll position.
      setActiveChapter(getChapterFromScrollPosition());
    },
    [],
  );

  useEffect(() => {
    const sections = chapterIds
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section !== null);

    if (sections.length === 0) return;

    const updateActiveChapter = () => {
      if (pinnedChapterRef.current !== null) return;
      syncActiveFromSpy();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id as Chapter;
          ratiosRef.current[id] = entry.isIntersecting ? entry.intersectionRatio : 0;
        }
        updateActiveChapter();
      },
      {
        rootMargin: `${STICKY_NAV_OFFSET}px 0px -${Math.round((1 - SCROLL_SPY_VIEWPORT_RATIO) * 100)}% 0px`,
        threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 1],
      },
    );

    for (const section of sections) {
      observer.observe(section);
    }

    return () => {
      observer.disconnect();
      pinCleanupRef.current?.();
      pinCleanupRef.current = null;
    };
  }, [syncActiveFromSpy]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, id: Chapter) => {
      event.preventDefault();
      trackEvent(GA_EVENTS.navClick, {
        [GA_PARAMS.chapterId]: id,
        [GA_PARAMS.method]: GA_NAV_METHOD.stickyNav,
      });

      // Optimistic active chapter + aria-current; keep pinned until scroll ends.
      pinCleanupRef.current?.();
      pinCleanupRef.current = null;

      const generation = pinGenerationRef.current + 1;
      pinGenerationRef.current = generation;
      pinnedChapterRef.current = id;
      setActiveChapter(id);

      const timeoutId = window.setTimeout(() => {
        releasePin(generation, 'timeout');
      }, STICKY_NAV_SCROLL_PIN_TIMEOUT_MS);

      // User wheel/touch takes over Lenis — release pin so spy tracks again.
      const onUserScrollIntent = () => {
        releasePin(generation, 'interrupt');
      };
      window.addEventListener('wheel', onUserScrollIntent, { passive: true });
      window.addEventListener('touchstart', onUserScrollIntent, {
        passive: true,
      });

      pinCleanupRef.current = () => {
        window.clearTimeout(timeoutId);
        window.removeEventListener('wheel', onUserScrollIntent);
        window.removeEventListener('touchstart', onUserScrollIntent);
      };

      scrollToSection(id, {
        offset: STICKY_NAV_OFFSET,
        reducedMotion: prefersReducedMotion,
        userData: SCROLL_PIN_USER_DATA,
        onComplete: () => {
          releasePin(generation, 'complete');
        },
      });
    },
    [prefersReducedMotion, releasePin],
  );

  // HERO_REVEAL_PROPS: fades with Introduction / Flyways via hero runway chrome scrub.
  // HERO_SWIRL_CLIP_TARGET_PROPS: clipped to the swirl (separate clipPath —
  // userSpaceOnUse is relative to this element's border box).
  // Hidden below sm via CSS — mobile chapter nav is Navbar MobileMenu.
  return (
    <nav
      className={styles.nav}
      data-active-chapter={activeChapter}
      aria-label={ariaLabel}
      {...HERO_REVEAL_PROPS}
      {...HERO_SWIRL_CLIP_TARGET_PROPS}
    >
      <ul
        className={styles.list}
        ref={listRef}
        data-pill-ready={pill.ready ? 'true' : undefined}
      >
        <li
          className={styles.pillSlot}
          aria-hidden="true"
          style={
            pill.ready
              ? ({
                  '--sticky-nav-pill-offset': `${pill.offset}px`,
                  '--sticky-nav-pill-width': `${pill.width}px`,
                } as React.CSSProperties)
              : undefined
          }
        >
          <span className={styles.pill} />
        </li>
        {items.map((item) => {
          const isActive = item.id === activeChapter;

          return (
            <li key={item.id} className={styles.item}>
              <a
                ref={(node) => setLinkRef(item.id, node)}
                href={item.href}
                className={[styles.link, isActive ? styles.linkActive : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-current={isActive ? 'true' : undefined}
                onClick={(event) => handleClick(event, item.id)}
              >
                <span className={styles.label}>{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
